import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal      from "@react/shared/ui/Modal"
import Button     from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import Badge      from "@react/shared/ui/Badge"
import {
  createReserve, updateReserve,
  uploadReservePhoto, destroyReservePhoto,
} from "../api/reserves"
import { dateFmt } from "../helpers/format"

const SEVERITY_OPTIONS = ["low", "medium", "high"]
const todayISO = () => new Date().toISOString().slice(0, 10)

const PhotoTile = ({ photo, onDelete, canManage }) => (
  <div className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden">
    <div className="block w-full aspect-[4/3] bg-slate-100 overflow-hidden">
      {photo.image_url
        ? <img src={photo.image_url} alt={photo.caption || ""} className="w-full h-full object-cover" />
        : <div className="w-full h-full grid place-items-center text-slate-400">
            <FontAwesomeIcon icon="image" />
          </div>}
    </div>
    <div className="p-2 text-[10.5px] text-slate-500 flex items-center justify-between">
      <span className="truncate" title={photo.caption}>{photo.caption || dateFmt(photo.taken_at)}</span>
    </div>
    {canManage && (
      <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <IconButton tone="danger" size="sm" onClick={() => onDelete(photo)} title="">
          <FontAwesomeIcon icon="trash" />
        </IconButton>
      </div>
    )}
  </div>
)

const PhotosColumn = ({ title, phase, photos, projectId, reserveId, canManage, onChange }) => {
  const fileRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  const handleFiles = (files) => {
    if (!files || files.length === 0 || !reserveId) return
    setUploading(true)
    const uploads = Array.from(files).map(file =>
      uploadReservePhoto(projectId, reserveId, file, null, todayISO(), phase)
    )
    Promise.all(uploads).then(responses => {
      const latest = responses[responses.length - 1]
      if (latest && !latest.errors) onChange(latest)
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ""
    })
  }

  const handleDelete = (photo) => {
    if (!confirm(I18n.t("projects.reserves.photos.confirm_destroy"))) return
    destroyReservePhoto(projectId, reserveId, photo.id).then(response => {
      if (response && !response.errors) onChange(response)
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{title}</span>
        {canManage && reserveId && (
          <button
            type      = "button"
            disabled  = {uploading}
            onClick   = {() => fileRef.current?.click()}
            className = "text-[12px] text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={uploading ? "spinner" : "plus"} spin={uploading} />
            {I18n.t("projects.reserves.photos.add")}
          </button>
        )}
        <input
          ref      = {fileRef}
          type     = "file"
          accept   = "image/*"
          multiple
          onChange = {e => handleFiles(e.target.files)}
          className= "hidden"
        />
      </div>
      {photos.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center text-[12px] text-slate-400">
          {I18n.t(`projects.reserves.photos.empty_${phase}`)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {photos.map(p => (
            <PhotoTile key={p.id} photo={p} onDelete={handleDelete} canManage={canManage} />
          ))}
        </div>
      )}
    </div>
  )
}

const ReserveModal = ({ isOpen, mode, projectId, reserve, tasks, canManage, onClose, onSaved }) => {
  const [title,       setTitle]       = useState("")
  const [description, setDescription] = useState("")
  const [location,    setLocation]    = useState("")
  const [severity,    setSeverity]    = useState("medium")
  const [taskId,      setTaskId]      = useState("")
  const [raisedOn,    setRaisedOn]    = useState(todayISO())
  const [dueDate,     setDueDate]     = useState("")
  const [saving,      setSaving]      = useState(false)
  const [errors,      setErrors]      = useState([])
  const [currentReserve, setCurrentReserve] = useState(reserve)

  useEffect(() => {
    if (!isOpen) return
    setErrors([])
    setCurrentReserve(reserve)
    if (mode === "edit" && reserve) {
      setTitle(reserve.title || "")
      setDescription(reserve.description || "")
      setLocation(reserve.location || "")
      setSeverity(reserve.severity || "medium")
      setTaskId(reserve.project_task_id ? String(reserve.project_task_id) : "")
      setRaisedOn(reserve.raised_on || todayISO())
      setDueDate(reserve.due_date || "")
    } else {
      setTitle(""); setDescription(""); setLocation("")
      setSeverity("medium"); setTaskId(""); setRaisedOn(todayISO()); setDueDate("")
    }
  }, [isOpen, mode, reserve])

  const handleSave = () => {
    if (saving) return
    if (!title) { setErrors([I18n.t("projects.reserves.errors.title_required")]); return }
    setSaving(true)
    setErrors([])

    const body = {
      title, description, location, severity,
      project_task_id: taskId || null,
      raised_on:       raisedOn || null,
      due_date:        dueDate  || null,
    }

    const request = (mode === "edit" && currentReserve)
      ? updateReserve(projectId, currentReserve.id, body)
      : createReserve(projectId, body)

    request
      .then(response => {
        if (response?.errors) {
          setErrors(Array.isArray(response.errors) ? response.errors : [String(response.errors)])
          return
        }
        setCurrentReserve(response)
        onSaved && onSaved(response)
      })
      .catch(() => setErrors([I18n.t("projects.reserves.errors.network")]))
      .finally(() => setSaving(false))
  }

  const onPhotoChange = (updatedReserve) => {
    setCurrentReserve(updatedReserve)
    onSaved && onSaved(updatedReserve)
  }

  const footer = (
    <>
      <div className="text-[12px] text-slate-500">
        {currentReserve?.number && (
          <span>{I18n.t("projects.reserves.form.identifier", { number: currentReserve.number })}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("projects.reserves.form.close")}</Button>
        {canManage && (
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            <FontAwesomeIcon icon={saving ? "spinner" : "check"} spin={saving} />
            {mode === "edit" || currentReserve
              ? I18n.t("projects.reserves.form.save")
              : I18n.t("projects.reserves.form.create")}
          </Button>
        )}
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {mode === "edit"
        ? I18n.t("projects.reserves.form.eyebrow_edit", { number: currentReserve?.number || "" })
        : I18n.t("projects.reserves.form.eyebrow_new")}
      title   = {currentReserve?.title || title || I18n.t("projects.reserves.form.title_new")}
      footer  = {footer}
    >
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-9">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.reserves.form.title_field")} <span className="text-rose-500">*</span>
          </label>
          <input
            value     = {title}
            onChange  = {e => setTitle(e.target.value)}
            disabled  = {!canManage}
            placeholder = {I18n.t("projects.reserves.form.title_placeholder")}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-3">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.reserves.form.severity")}
          </label>
          <select
            value     = {severity}
            onChange  = {e => setSeverity(e.target.value)}
            disabled  = {!canManage}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
          >
            {SEVERITY_OPTIONS.map(s => (
              <option key={s} value={s}>{I18n.t(`projects.reserves.severities.${s}`)}</option>
            ))}
          </select>
        </div>

        <div className="col-span-6">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.reserves.form.task")}
          </label>
          <select
            value     = {taskId}
            onChange  = {e => setTaskId(e.target.value)}
            disabled  = {!canManage}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
          >
            <option value="">{I18n.t("projects.reserves.form.task_none")}</option>
            {(tasks || []).map(t => (
              <option key={t.id} value={t.id}>{t.title}</option>
            ))}
          </select>
        </div>

        <div className="col-span-6">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.reserves.form.location")}
          </label>
          <input
            value     = {location}
            onChange  = {e => setLocation(e.target.value)}
            disabled  = {!canManage}
            placeholder = {I18n.t("projects.reserves.form.location_placeholder")}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-12">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.reserves.form.description")}
          </label>
          <textarea
            value     = {description}
            onChange  = {e => setDescription(e.target.value)}
            disabled  = {!canManage}
            rows      = "3"
            placeholder = {I18n.t("projects.reserves.form.description_placeholder")}
            className = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-6">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.reserves.form.raised_on")}
          </label>
          <input
            type      = "date"
            value     = {raisedOn || ""}
            onChange  = {e => setRaisedOn(e.target.value)}
            disabled  = {!canManage}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>

        <div className="col-span-6">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.reserves.form.due_date")}
          </label>
          <input
            type      = "date"
            value     = {dueDate || ""}
            onChange  = {e => setDueDate(e.target.value)}
            disabled  = {!canManage}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}

      {currentReserve && (
        <div className="mt-6 border-t border-slate-200 pt-4">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-3">
            {I18n.t("projects.reserves.photos.title")}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <PhotosColumn
              title     = {I18n.t("projects.reserves.photos.before")}
              phase     = "before"
              photos    = {currentReserve.photos?.before || []}
              projectId = {projectId}
              reserveId = {currentReserve.id}
              canManage = {canManage}
              onChange  = {onPhotoChange}
            />
            <PhotosColumn
              title     = {I18n.t("projects.reserves.photos.after")}
              phase     = "after"
              photos    = {currentReserve.photos?.after || []}
              projectId = {projectId}
              reserveId = {currentReserve.id}
              canManage = {canManage}
              onChange  = {onPhotoChange}
            />
          </div>
        </div>
      )}
    </Modal>
  )
}

export default ReserveModal
